import React, { useState, useEffect } from "react";
import { User } from "lucide-react";

interface AvatarWithBlurProps {
  src: string;
  alt: string;
  className?: string;
  fallbackIcon?: React.ReactNode;
  fallbackClassName?: string;
}

const AvatarWithBlur: React.FC<AvatarWithBlurProps> = ({
  src,
  alt,
  className = "w-7 h-7 object-cover rounded-md",
  fallbackIcon,
  fallbackClassName = "w-7 h-7 rounded-md bg-background-panel dark:bg-secondary/10 flex items-center justify-center",
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [src]);

  if (imageError) {
    return (
      <div className={fallbackClassName}>
        {fallbackIcon || (
          <User className="w-4 h-4 text-black dark:text-secondary" />
        )}
      </div>
    );
  }

  // Extract width/height classes for placeholder
  const sizeMatch = className.match(/(w-\d+|h-\d+)/g);
  const sizeClasses = sizeMatch ? sizeMatch.join(" ") : "w-full h-full";
  const roundedMatch = className.match(/rounded-\w+/);
  const roundedClass = roundedMatch ? roundedMatch[0] : "rounded-md";

  return (
    <div className="relative w-full h-full">
      {/* Blur placeholder - animated pulse */}
      {!imageLoaded && (
        <div
          className={`absolute inset-0 ${sizeClasses} ${roundedClass} bg-background-panel dark:bg-background-panelDark animate-pulse`}
        />
      )}
      {/* Actual image */}
      <img
        src={src}
        alt={alt}
        className={`${className} transition-opacity duration-300 ${
          imageLoaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={() => setImageLoaded(true)}
        onError={() => {
          setImageError(true);
          setImageLoaded(false);
        }}
      />
    </div>
  );
};

export default AvatarWithBlur;
